import { type ReactNode } from "react";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus, Search, Loader2 } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@/lib/studio-constants";
import type { ContentTypeConfig } from "@/lib/studio-constants";
import { studioContentStatuses } from "@/lib/schema";
import { PaginationControls } from "@/components/pagination-controls";

interface FilterConfig {
  key: string;
  label: string;
  options: { value: string; label: string }[];
  width?: string;
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  startIndex: number;
  endIndex: number;
  canGoNext: boolean;
  canGoPrev: boolean;
  onPageChange: (page: number) => void;
  onNext: () => void;
  onPrev: () => void;
}

interface ContentListShellProps {
  contentTypeConfig: ContentTypeConfig;
  isLoading: boolean;
  itemCount: number;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filters?: FilterConfig[];
  filterValues?: Record<string, string>;
  onFilterChange?: (key: string, value: string) => void;
  showLanguageFilter?: boolean;
  languageFilterValue?: string;
  onLanguageFilterChange?: (value: string) => void;
  emptyStateMessage?: string;
  headerActions?: ReactNode;
  pagination?: PaginationProps;
  children: ReactNode;
}

export function ContentListShell({
  contentTypeConfig,
  isLoading,
  itemCount,
  searchQuery,
  onSearchChange,
  filters = [],
  filterValues = {},
  onFilterChange,
  showLanguageFilter = true,
  languageFilterValue = "all",
  onLanguageFilterChange,
  emptyStateMessage,
  headerActions,
  pagination,
  children,
}: ContentListShellProps) {
  const Icon = contentTypeConfig.icon;

  return (
    <div
      className="p-6 space-y-6 max-w-6xl mx-auto"
      data-testid={`${contentTypeConfig.key}-list-page`}
    >
      <div className="flex items-center gap-4 flex-wrap">
        <Link href="/studio">
          <Button variant="ghost" size="icon" data-testid="button-back-studio">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <h1
            className="font-serif text-2xl font-semibold text-foreground"
            data-testid={`text-${contentTypeConfig.key}-title`}
          >
            {contentTypeConfig.pluralLabel}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Create and manage {contentTypeConfig.label.toLowerCase()} content
          </p>
        </div>
        {headerActions}
        <Link href={contentTypeConfig.createRoute}>
          <Button
            data-testid={`button-create-${contentTypeConfig.key}`}
            className="evo-gradient text-white hover:opacity-90"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create {contentTypeConfig.label}
          </Button>
        </Link>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={`Search ${contentTypeConfig.pluralLabel.toLowerCase()}...`}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-9"
                data-testid={`input-search-${contentTypeConfig.key}`}
              />
            </div>
            <Select
              value={filterValues["status"] ?? "all"}
              onValueChange={(val) => onFilterChange?.("status", val)}
            >
              <SelectTrigger
                className="w-[150px]"
                data-testid="select-status-filter"
              >
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {studioContentStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filters.map((filter) => (
              <Select
                key={filter.key}
                value={filterValues[filter.key] ?? "all"}
                onValueChange={(val) => onFilterChange?.(filter.key, val)}
              >
                <SelectTrigger
                  className="w-[180px]"
                  style={filter.width ? { width: filter.width } : undefined}
                  data-testid={`select-${filter.key}-filter`}
                >
                  <SelectValue placeholder={filter.label} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All {filter.label}</SelectItem>
                  {filter.options.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ))}
            {showLanguageFilter && onLanguageFilterChange && (
              <Select
                value={languageFilterValue}
                onValueChange={onLanguageFilterChange}
              >
                <SelectTrigger
                  className="w-[160px]"
                  data-testid="select-language-filter"
                >
                  <SelectValue placeholder="Language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Languages</SelectItem>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div
              className="flex items-center justify-center py-16"
              data-testid="list-loading"
            >
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : itemCount === 0 ? (
            <div
              className="flex flex-col items-center justify-center py-16 text-center"
              data-testid="list-empty"
            >
              <div className="p-4 rounded-full bg-muted mb-4">
                <Icon className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-1">
                {emptyStateMessage ||
                  `No ${contentTypeConfig.label.toLowerCase()} content yet`}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first {contentTypeConfig.label.toLowerCase()} to get
                started
              </p>
              <Link href={contentTypeConfig.createRoute}>
                <Button
                  data-testid={`button-create-first-${contentTypeConfig.key}`}
                  className="evo-gradient text-white hover:opacity-90"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create {contentTypeConfig.label}
                </Button>
              </Link>
            </div>
          ) : (
            <>
              {children}
              {pagination && (
                <PaginationControls
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  startIndex={pagination.startIndex}
                  endIndex={pagination.endIndex}
                  canGoNext={pagination.canGoNext}
                  canGoPrev={pagination.canGoPrev}
                  onPageChange={pagination.onPageChange}
                  onNext={pagination.onNext}
                  onPrev={pagination.onPrev}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
